import {
  isInPast,
  startOfDateInLocalTimezone,
  toDateString,
} from '@seen/core/dates'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  DISENROLLMENT_REASONS,
  type DisenrollmentReason,
} from '@seen/core/models/patients/constants'
import {
  ExtendedProvenance,
  ProvenanceParticipantType,
  provenanceActivityDisenrollmentScheduled,
} from '@seen/core/models/provenance'
import { now } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { addMonths, startOfMonth } from 'date-fns'
import { type JSX, useCallback, useState } from 'react'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { PatientCard } from '#src/components/patient/PatientCard.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDisenrollDialogStore } from '#src/hooks/useDisenrollDialogStore.ts'
import { useBatchMutation } from '#src/hooks/useFhirBundleMutation.ts'

export function DisenrollDialog(): JSX.Element {
  const isOpen = useDisenrollDialogStore((state) => state.isOpen)
  const participant = useDisenrollDialogStore((state) => state.participant)
  const close = useDisenrollDialogStore((state) => state.close)

  const practitioner = useCurrentPractitioner()
  const batchMutation = useBatchMutation()

  const defaultDate = toDateString(addMonths(startOfMonth(now()), 1))

  const [disenrollmentDate, setDisenrollmentDate] = useState(defaultDate)
  const [reason, setReason] = useState<DisenrollmentReason | null>(null)
  const [deceasedDate, setDeceasedDate] = useState<string | null>(null)
  const [note, setNote] = useState(`Disenrolling as of ${defaultDate}`)
  const [hasUserEditedNote, setHasUserEditedNote] = useState(false)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close()
        setReason(null)
        setDeceasedDate(null)
        setNote(`Disenrolling as of ${defaultDate}`)
        setHasUserEditedNote(false)
        setDisenrollmentDate(defaultDate)
      }
    },
    [close, defaultDate]
  )

  const handleDateChange = (dateString: string | null): void => {
    if (dateString) {
      const newDate = startOfDateInLocalTimezone(dateString)
      setDisenrollmentDate(newDate)
      if (!hasUserEditedNote) {
        setNote(`Disenrolling as of ${toDateString(newDate)}`)
      }
    }
  }

  const handleNoteChange = (newNote: string): void => {
    setNote(newNote)
    setHasUserEditedNote(newNote !== `Disenrolling as of ${disenrollmentDate}`)
  }

  const handleConfirm = (): void => {
    if (!participant) {
      return
    }
    batchMutation.mutate((batch) => {
      batch.patch(participant, (p) => {
        p.disenrollmentDate = disenrollmentDate
        if (reason) {
          p.disenrollmentReason = reason
        }
        if (reason === 'deceased' && deceasedDate) {
          p.deceasedDateTime = deceasedDate
        }
      })

      const timelineNote = new CommentCommunication({
        partOf: [participant.reference],
        message: note.trim(),
        sender: practitioner.reference,
      })
      batch.create(timelineNote)

      batch.create(
        new ExtendedProvenance({
          target: participant.reference,
          who: practitioner.reference,
          type: ProvenanceParticipantType.AUTHOR,
          activity: provenanceActivityDisenrollmentScheduled,
        })
      )
    })
    handleOpenChange(false)
  }

  const reasonOptions = DISENROLLMENT_REASONS.map((r) => ({
    value: r.value,
    label: r.label,
  }))

  const canConfirm = note.trim().length > 0

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogDisplay>
        <DialogHeader asChild>
          <DialogTitle>
            <p>Disenroll Participant</p>
          </DialogTitle>
        </DialogHeader>
        <DialogContent className="py-0">
          <DialogDescription>
            {participant && (
              <div className="flex flex-col gap-4">
                <Card>
                  <PatientCard participant={participant} />
                </Card>
                <span>
                  Are you sure you want to disenroll this participant? You can
                  adjust the disenrollment date below.
                </span>
              </div>
            )}
          </DialogDescription>
          <div className="mb-4 grid gap-2">
            <label htmlFor="disenrollmentDate" className="text-sm font-medium">
              Disenrollment Date
            </label>
            <DatePicker
              value={disenrollmentDate}
              onChange={handleDateChange}
              className="w-full"
              clearText=""
            />
            {isInPast(disenrollmentDate) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <strong>Warning:</strong> The disenrollment will be processed
                and active as of today, but the analytics will be back dated.
                Are you sure you want to do that?
              </div>
            )}
          </div>
          <div className="mb-4 grid gap-2">
            <label htmlFor="reason" className="text-sm font-medium">
              Disenrollment Reason
            </label>
            <ComboboxButton
              options={reasonOptions}
              value={reason}
              onChange={setReason}
              placeholder="Select reason…"
              className="w-full justify-start"
            />
          </div>
          {reason === 'deceased' && (
            <div className="mb-4 grid gap-2">
              <label htmlFor="deceasedDate" className="text-sm font-medium">
                Deceased Date
              </label>
              <DatePicker
                value={deceasedDate}
                onChange={(val) =>
                  setDeceasedDate(val ? startOfDateInLocalTimezone(val) : null)
                }
                className="w-full"
              />
            </div>
          )}
          <div className="grid gap-2">
            <label htmlFor="note" className="text-sm font-medium">
              Note <span className="text-red-500">*</span>
            </label>
            <Input
              id="note"
              className="mt-1"
              placeholder="Enter your note here..."
              value={note}
              onChange={(e) => handleNoteChange(e.target.value)}
            />
            {note.trim().length === 0 && (
              <p className="text-sm text-red-500">Note is required</p>
            )}
          </div>
        </DialogContent>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}
